import { ANPREventQueryParams } from 'features/anpr-events/types/anpr-event-query-param'
import { useAuth } from 'layout/context/auth-context'
import { useLoading } from 'layout/context/loading-context'
import { useSignalR } from 'layout/context/signalr-context'
import { useEffect, useState } from 'react'
import { getBrazilDate } from 'shared/utils/get-brazil-date'
import { VehicleCounts } from 'domain/vehicle-counts'
import { useAHCREvent } from '../../ahcr-events/hooks/useAHCREvent'
import { useANPREvent } from '../../anpr-events/hooks/useANPREvents'
import { CameraEvent } from '../types/camera-event'
import { CameraPerformance } from 'domain/camera-performance'
import { blankVehicleCount } from '../types/dashboard.types'
import { DateFilterState } from 'shared/date-filter/date-filter-component'

const currentDate: DateFilterState = {
  startDate: getBrazilDate('start'),
  endDate: getBrazilDate('end'),
  preset: 'today',
}

const yesterdayDate: DateFilterState = {
  startDate: getBrazilDate('start', -1),
  endDate: getBrazilDate('end', -1),
  preset: 'yesterday',
}

export interface UseDashboardHookReturn {
  cameraPerformance: CameraPerformance[]
  /** Mesma métrica, dia anterior — usada só para o comparativo "vs. ontem".
   *  Não é atualizado por SignalR: é uma foto fixa de um período fechado. */
  yesterdayCameraPerformance: CameraPerformance[]
  eventHistory: CameraEvent[]
  vehicleCount: VehicleCounts
  captureOnline: boolean
  signalRConnected: boolean
  error: string | null
}

export const useDashboard = (): UseDashboardHookReturn => {
  const { clientId } = useAuth()
  const { fetchANPREventsCount } = useANPREvent()
  const { fetchAHCREventsCount } = useAHCREvent()
  const { startLoading, stopLoading } = useLoading()
  const {
    vehicleCount,
    eventHistory,
    cameraPerformance,
    captureOnline,
    connected: signalRConnected,
    setVehicleCount,
    setCameraPerformance,
  } = useSignalR()

  const [error, setError] = useState<string | null>(null)
  const [yesterdayCameraPerformance, setYesterdayCameraPerformance] =
    useState<CameraPerformance[]>([])

  const sumVehicleCounts = (
    count1: VehicleCounts | undefined,
    count2: VehicleCounts | undefined
  ): VehicleCounts => {
    return {
      bikes: (count1?.bikes ?? 0) + (count2?.bikes ?? 0),
      buses: (count1?.buses ?? 0) + (count2?.buses ?? 0),
      cars: (count1?.cars ?? 0) + (count2?.cars ?? 0),
      people: (count1?.people ?? 0) + (count2?.people ?? 0),
      trucks: (count1?.trucks ?? 0) + (count2?.trucks ?? 0),
      unknown: (count1?.unknown ?? 0) + (count2?.unknown ?? 0),
    }
  }

  useEffect(() => {
    if (!clientId) {
      return
    }

    const params = {
      endDate: currentDate.endDate,
      startDate: currentDate.startDate,
      clientId: clientId,
    } as ANPREventQueryParams

    const yesterdayParams = {
      endDate: yesterdayDate.endDate,
      startDate: yesterdayDate.startDate,
      clientId: clientId,
    } as ANPREventQueryParams

    // Reset state
    startLoading('dashboard')
    setCameraPerformance([])
    setVehicleCount(blankVehicleCount)
    setYesterdayCameraPerformance([])
    setError(null)

    // Fetch initial data from both sources
    Promise.all([fetchANPREventsCount(params), fetchAHCREventsCount(params)])
      .then(([anprData, ahcrData]) => {
        // Combine camera performance data
        const combined = [
          ...(anprData?.cameraPerformance || []),
          ...(ahcrData?.cameraPerformance || []),
        ]

        setCameraPerformance(combined)

        // Combine vehicle counts
        const combinedCounts = sumVehicleCounts(
          anprData?.vehiclePerformance,
          ahcrData?.vehiclePerformance
        )

        setVehicleCount(combinedCounts)

        console.info('✅ Dashboard: Initial data loaded successfully')
      })
      .catch((err) => {
        console.error('❌ Dashboard: Failed to load initial data:', err)
        setError(
          'Falha ao carregar dados do dashboard. Por favor, tente novamente.'
        )
      })
      .finally(() => {
        stopLoading('dashboard')
      })

    // Comparativo "vs. ontem": período fechado, buscado uma vez só e à
    // parte do estado que o SignalR atualiza em tempo real.
    Promise.all([
      fetchANPREventsCount(yesterdayParams),
      fetchAHCREventsCount(yesterdayParams),
    ])
      .then(([anprData, ahcrData]) => {
        setYesterdayCameraPerformance([
          ...(anprData?.cameraPerformance || []),
          ...(ahcrData?.cameraPerformance || []),
        ])
      })
      .catch((err) => {
        // Não bloqueia o dashboard: o comparativo é um extra, não o dado principal.
        console.error(
          '⚠️ Dashboard: Falha ao carregar dados de ontem para comparativo:',
          err
        )
      })
  }, [clientId])

  return {
    cameraPerformance,
    yesterdayCameraPerformance,
    eventHistory,
    vehicleCount,
    captureOnline,
    signalRConnected,
    error,
  }
}
